import CollapsedPanel from '../components/CollapsedPanel';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';


import InputBlock from '../components/InputBlock';
import ColorPicker from '../components/ColorPicker';
import { EditorContext } from '../../../utils/contexts';
import NumberInput from '../components/NumberInput';
import Switcher from '../components/Switcher';
import { LabelSelector } from '../Steps/components';

const StateToggleWrapper = styled(LabelSelector)`
  margin-bottom: 20px;
  grid-template-columns: 1fr;
`;

const TypographyWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;


const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  background-color: #f6f6f6;
  .MuiToggleButton-root {
    border: 1px solid #e2e2e2;
    padding: 6px 10px;
    &.Mui-selected {
      background-color: #27ab9e;
      color: white;
      &:hover {
        background-color: #1e857a;
      }
    }
  }
`;

export default function ButtonStylingPanel({ open, title }) {
  const { settings, updateSettings } = useContext(EditorContext);
  const [activeState, setActiveState] = React.useState('default');

  const maxHeight = settings.type === 'Bar' ? settings.size : 100;

  const handleStateChange = (event, newState) => {
    if (newState !== null) {
      setActiveState(newState);
    }
  };

  const isDefault = activeState === 'default';

  const stripInlineColor = (htmlStr) => {
    if (typeof htmlStr !== 'string' || !htmlStr) return htmlStr;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlStr, 'text/html');
      const allElements = doc.body.querySelectorAll('*');
      allElements.forEach(el => {
        if (el.style && el.style.color) {
          el.style.color = ''; // Only strip text color
        }
        if (el.hasAttribute('color')) {
          el.removeAttribute('color');
        }
        if (el.hasAttribute('data-mce-style')) {
          // TinyMCE mirrors styles into `data-mce-style` attribute
          el.setAttribute('data-mce-style', el.getAttribute('style') || '');
        }
      });
      return doc.body.innerHTML;
    } catch(e) {
      return htmlStr.replace(/(^|[^a-z-])color:\s*[^;"]+;?/gi, '$1');
    }
  };

  const updateColor = (key, color) => {
    const hex = typeof color === 'string' ? color : (color.hex ? color.hex.replace('#', '') : color);
    const update = { [key]: hex };
    
    if (key === 'button_color') {
      update.secondary_color = hex;
      // ONLY sync to hover if hover color is not set yet OR it matches the old default color
      if (isDefault && (!settings.cta_hover_background_color || settings.cta_hover_background_color === settings.button_color)) {
        update.cta_hover_background_color = hex;
      }
    }
    
    if (key === 'link_color') {
      update.conversion_font_color = hex;
      // ONLY sync to hover if hover color is not set yet OR it matches the old default color
      if (isDefault && (!settings.cta_hover_text_color || settings.cta_hover_text_color === settings.link_color)) {
        update.cta_hover_text_color = hex;
      }
      
      // Auto-strip top-toolbar rich-text color formatting when the side-panel color picker is explicitly used
      if (isDefault) {
        if (settings.link_text) update.link_text = stripInlineColor(settings.link_text);
        if (settings.conversion_cta_text) update.conversion_cta_text = stripInlineColor(settings.conversion_cta_text);
        if (settings.cta_text) update.cta_text = stripInlineColor(settings.cta_text);
        if (settings.button_text) update.button_text = stripInlineColor(settings.button_text);
        if (settings.caption) update.caption = stripInlineColor(settings.caption);
      }
    }
    
    updateSettings(update);
  };

  const updateNumeric = (key, value) => {
    const update = { [key]: value };
    // Smart Sync: Only sync to hover if it's not customized or matches the current value being changed
    if (isDefault) {
      if (key === 'cta_border_radius' && (!settings.cta_hover_border_radius || settings.cta_hover_border_radius === settings.cta_border_radius)) {
        update.cta_hover_border_radius = value;
      }
      if (key === 'cta_border_width' && (settings.cta_hover_border_width === undefined || settings.cta_hover_border_width === null || settings.cta_hover_border_width === settings.cta_border_width)) {
        update.cta_hover_border_width = value;
      }
    }
    updateSettings(update);
  };

  const toggleBoolean = (key) => {
    updateSettings({ [key]: !settings[key] });
  };

  const stripInlineTypography = (htmlStr, format) => {
    if (typeof htmlStr !== 'string' || !htmlStr) return htmlStr;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlStr, 'text/html');
      
      const stripTags = (tags) => {
        tags.forEach(tag => {
          doc.body.querySelectorAll(tag).forEach(el => {
            const fragment = document.createDocumentFragment();
            while (el.firstChild) fragment.appendChild(el.firstChild);
            el.parentNode.replaceChild(fragment, el);
          });
        });
      };
      
      const stripStyles = (prop) => {
        doc.body.querySelectorAll('*').forEach(el => {
          if (el.style && el.style[prop]) el.style[prop] = '';
          if (el.hasAttribute('data-mce-style')) {
             el.setAttribute('data-mce-style', el.getAttribute('style') || '');
          }
        });
      };

      if (format === 'bold') {
        stripTags(['strong', 'b']);
        stripStyles('fontWeight');
      }
      if (format === 'italic') {
        stripTags(['em', 'i']);
        stripStyles('fontStyle');
      }
      if (format === 'underline') {
        stripTags(['u']);
        stripStyles('textDecoration');
      }
      return doc.body.innerHTML;
    } catch(e) {
      return htmlStr;
    }
  };

  const toggleStyle = (style) => {
    const key = isDefault ? `cta_${style}` : `cta_hover_${style}`;
    
    // Compare against the actively parsed HTML/UI state, not just raw database properties
    const isCurrentlyActive = getStyleValues()[style];
    const newValue = !isCurrentlyActive;
    
    const update = { [key]: newValue };
    
    if (isDefault) {
      update[`cta_hover_${style}`] = newValue; // Sync to hover if editing default state
      
      // Actively strip out rich-text tags if Side Panel style is toggled, preventing HTML locking
      if (settings.link_text) update.link_text = stripInlineTypography(settings.link_text, style);
      if (settings.conversion_cta_text) update.conversion_cta_text = stripInlineTypography(settings.conversion_cta_text, style);
      if (settings.cta_text) update.cta_text = stripInlineTypography(settings.cta_text, style);
      if (settings.button_text) update.button_text = stripInlineTypography(settings.button_text, style);
      if (settings.caption) update.caption = stripInlineTypography(settings.caption, style);
    }
    
    updateSettings(update);
  };

  const isTrue = (val) => val === true || val === 1 || val === 'true' || val === '1';

  const getStyleValues = () => {
    const prefix = isDefault ? 'cta_' : 'cta_hover_';
    let bold = isTrue(settings[`${prefix}bold`]);
    let italic = isTrue(settings[`${prefix}italic`]);
    let underline = isTrue(settings[`${prefix}underline`]);

    // Dynamically sync Typography UI states from top-toolbar HTML natively without database conflict limits
    if (isDefault) {
      const activeTextHtml = settings.link_text || settings.conversion_cta_text || settings.cta_text || settings.button_text || settings.caption || '';
      if (activeTextHtml) {
        if (/<(strong|b)\b/i.test(activeTextHtml) || /font-weight:\s*(bold|[789]00)/i.test(activeTextHtml)) bold = true;
        if (/<(em|i)\b/i.test(activeTextHtml) || /font-style:\s*italic/i.test(activeTextHtml)) italic = true;
        if (/<u\b/i.test(activeTextHtml) || /text-decoration:\s*underline/i.test(activeTextHtml)) underline = true;
      }
    }

    return { bold, italic, underline };
  };

  const currentStyles = getStyleValues();
  const selectedStyles = [
    ...(currentStyles.bold ? ['bold'] : []),
    ...(currentStyles.italic ? ['italic'] : []),
    ...(currentStyles.underline ? ['underline'] : []),
  ];

  const updateBorderColor = (color) => {
    const hex = color.hex ? color.hex.replace('#', '') : color;
    const key = isDefault ? 'cta_border_color' : 'cta_hover_border_color';
    const update = { [key]: hex };
    
    // ONLY sync to hover if hover color is not set yet OR it matches the old default color
    if (isDefault && (!settings.cta_hover_border_color || settings.cta_hover_border_color === settings.cta_border_color)) {
      update.cta_hover_border_color = hex;
    }
    
    updateSettings(update);
  };

  const getDisplayTextColor = () => {
    if (!isDefault) return settings.cta_hover_text_color || settings.link_color;
    
    // Check rich-text HTML for a Top-Toolbar inline color
    const activeTextHtml = settings.link_text || settings.conversion_cta_text || settings.cta_text || settings.button_text || settings.caption || '';
    const match = activeTextHtml.match(/color:\s*(#[a-fA-F0-9]{3,6}|rgb[a]?\([^)]+\))/i);
    if (match) {
      let extracted = match[1].trim();
      if (extracted.toLowerCase().startsWith('rgb')) {
        const digits = extracted.match(/\d+/g);
        if (digits && digits.length >= 3) {
          extracted = ((1 << 24) + (parseInt(digits[0], 10) << 16) + (parseInt(digits[1], 10) << 8) + parseInt(digits[2], 10)).toString(16).slice(1);
        }
      }
      return extracted.replace('#', ''); // ColorPicker handles hex without hash
    }
    return settings.link_color;
  };

  return (
    <CollapsedPanel open={open || false} title={title || 'CTA Button Styling'}>
      <StateToggleWrapper>
        <StyledToggleButtonGroup
          value={activeState}
          exclusive
          onChange={handleStateChange}
          aria-label="button state"
          size="small"
          fullWidth
        >
          <ToggleButton value="default" aria-label="default state">
            Default
          </ToggleButton>
          <ToggleButton value="hover" aria-label="hover state">
            Hover
          </ToggleButton>
        </StyledToggleButtonGroup>
      </StateToggleWrapper>

      <InputBlock label={isDefault ? 'Background Color' : 'Hover Background Color'}>
        <ColorPicker
          value={isDefault ? settings.button_color : (settings.cta_hover_background_color || settings.button_color)}
          onChange={(color) => updateColor(isDefault ? 'button_color' : 'cta_hover_background_color', color)}
          size="small"
        />
      </InputBlock>

      <InputBlock label={isDefault ? 'Text Color' : 'Hover Text Color'}>
        <ColorPicker
          value={getDisplayTextColor()}
          onChange={(color) => updateColor(isDefault ? 'link_color' : 'cta_hover_text_color', color)}
          size="small"
        />
      </InputBlock>

      <InputBlock label="Typography">
        <TypographyWrapper>
          <StyledToggleButtonGroup
            value={selectedStyles}
            onChange={(e, newStyles) => {
              const style = ['bold', 'italic', 'underline'].find(s => 
                (newStyles.includes(s) && !selectedStyles.includes(s)) || 
                (!newStyles.includes(s) && selectedStyles.includes(s))
              );
              if (style) toggleStyle(style);
            }}
            aria-label="text formatting"
            size="small"
          >
            <ToggleButton value="bold" aria-label="bold">
              <FormatBoldIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="italic" aria-label="italic">
              <FormatItalicIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="underline" aria-label="underline">
              <FormatUnderlinedIcon fontSize="small" />
            </ToggleButton>
          </StyledToggleButtonGroup>
        </TypographyWrapper>
      </InputBlock>

      {isDefault && (
        <InputBlock label="Shadow">
          <Switcher
            checked={settings.cta_shadow_enabled}
            onChange={() => toggleBoolean('cta_shadow_enabled')}
          />
        </InputBlock>
      )}

      <InputBlock label={isDefault ? 'All rounded corners' : 'All hover rounded corners'}>
        <Switcher
          checked={isDefault ? !!settings.cta_border_radius_linked || settings.cta_border_radius_linked === undefined : !!settings.cta_hover_border_radius_linked || settings.cta_hover_border_radius_linked === undefined}
          onChange={() => {
            const key = isDefault ? 'cta_border_radius_linked' : 'cta_hover_border_radius_linked';
            const currentlyLinked = isDefault ? (!!settings.cta_border_radius_linked || settings.cta_border_radius_linked === undefined) : (!!settings.cta_hover_border_radius_linked || settings.cta_hover_border_radius_linked === undefined);
            const update = { [key]: !currentlyLinked };
            
            if (!currentlyLinked) {
              const prefix = isDefault ? 'cta_' : 'cta_hover_';
              const uniformVal = isDefault ? settings.cta_border_radius : (settings.cta_hover_border_radius ?? settings.cta_border_radius);
              update[`${prefix}border_radius_top_left`] = uniformVal;
              update[`${prefix}border_radius_top_right`] = uniformVal;
              update[`${prefix}border_radius_bottom_right`] = uniformVal;
              update[`${prefix}border_radius_bottom_left`] = uniformVal;
            }
            updateSettings(update);
          }}
        />
      </InputBlock>

      {(isDefault ? (!!settings.cta_border_radius_linked || settings.cta_border_radius_linked === undefined) : (!!settings.cta_hover_border_radius_linked || settings.cta_hover_border_radius_linked === undefined)) ? (
        <InputBlock label={isDefault ? 'Rounded corners' : 'Hover Rounded corners'}>
          <NumberInput
            value={isDefault ? settings.cta_border_radius : (settings.cta_hover_border_radius ?? settings.cta_border_radius)}
            onChange={(val) => {
              const prefix = isDefault ? 'cta_' : 'cta_hover_';
              const update = {
                [`${prefix}border_radius`]: val,
                [`${prefix}border_radius_top_left`]: val,
                [`${prefix}border_radius_top_right`]: val,
                [`${prefix}border_radius_bottom_right`]: val,
                [`${prefix}border_radius_bottom_left`]: val,
              };
              if (isDefault) {
                const hoverIsLinked = !!settings.cta_hover_border_radius_linked || settings.cta_hover_border_radius_linked === undefined;
                if (hoverIsLinked && (!settings.cta_hover_border_radius || settings.cta_hover_border_radius === settings.cta_border_radius)) {
                  update.cta_hover_border_radius = val;
                  update.cta_hover_border_radius_top_left = val;
                  update.cta_hover_border_radius_top_right = val;
                  update.cta_hover_border_radius_bottom_right = val;
                  update.cta_hover_border_radius_bottom_left = val;
                }
              }
              updateSettings(update);
            }}
            max={45}
          />
        </InputBlock>
      ) : (
        <>
          <InputBlock label={isDefault ? 'Top Left corners' : 'Hover Top Left corners'}>
            <NumberInput
              value={isDefault ? (settings.cta_border_radius_top_left ?? 0) : (settings.cta_hover_border_radius_top_left ?? settings.cta_border_radius_top_left ?? 0)}
              onChange={(val) => updateSettings({ [isDefault ? 'cta_border_radius_top_left' : 'cta_hover_border_radius_top_left']: val })}
              max={45}
            />
          </InputBlock>
          <InputBlock label={isDefault ? 'Top Right corners' : 'Hover Top Right corners'}>
            <NumberInput
              value={isDefault ? (settings.cta_border_radius_top_right ?? 0) : (settings.cta_hover_border_radius_top_right ?? settings.cta_border_radius_top_right ?? 0)}
              onChange={(val) => updateSettings({ [isDefault ? 'cta_border_radius_top_right' : 'cta_hover_border_radius_top_right']: val })}
              max={45}
            />
          </InputBlock>
          <InputBlock label={isDefault ? 'Bottom Right corners' : 'Hover Bottom Right corners'}>
            <NumberInput
              value={isDefault ? (settings.cta_border_radius_bottom_right ?? 0) : (settings.cta_hover_border_radius_bottom_right ?? settings.cta_border_radius_bottom_right ?? 0)}
              onChange={(val) => updateSettings({ [isDefault ? 'cta_border_radius_bottom_right' : 'cta_hover_border_radius_bottom_right']: val })}
              max={45}
            />
          </InputBlock>
          <InputBlock label={isDefault ? 'Bottom Left corners' : 'Hover Bottom Left corners'}>
            <NumberInput
              value={isDefault ? (settings.cta_border_radius_bottom_left ?? 0) : (settings.cta_hover_border_radius_bottom_left ?? settings.cta_border_radius_bottom_left ?? 0)}
              onChange={(val) => updateSettings({ [isDefault ? 'cta_border_radius_bottom_left' : 'cta_hover_border_radius_bottom_left']: val })}
              max={45}
            />
          </InputBlock>
        </>
      )}

      <InputBlock label={isDefault ? 'Border color' : 'Hover Border color'}>
        <ColorPicker
          value={isDefault ? settings.cta_border_color : (settings.cta_hover_border_color || settings.cta_border_color)}
          onChange={updateBorderColor}
        />
      </InputBlock>

      <InputBlock label={isDefault ? 'Border width' : 'Hover Border width'}>
        <NumberInput
          value={isDefault ? settings.cta_border_width : (settings.cta_hover_border_width ?? settings.cta_border_width)}
          onChange={(val) => updateNumeric(isDefault ? 'cta_border_width' : 'cta_hover_border_width', val)}
          max={10}
          size="small"
        />
      </InputBlock>

      {isDefault && (
        <InputBlock label="Height">
          <NumberInput
            value={settings.cta_height}
            onChange={(val) => updateNumeric('cta_height', val)}
            max={maxHeight}
          />
        </InputBlock>
      )}
    </CollapsedPanel>
  );
}
